import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  api,
  type Betreuungsart,
  type Betreuungseingabe,
  type Betreuungsmonat,
  type Betreuungsverhaeltnis,
  type Betreuungsvorschlag,
  type BueroKunde
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'
import {
  formatChf,
  alsBruch,
  mengentext,
  aufteilungstext,
  eingewoehnungszeitraum,
  eingewoehnungsendeVorschlag,
  EINGEWOEHNUNG_TAGE,
  EINGEWOEHNUNG_PAUSCHALE_RP,
  BETREUUNGSART_LABEL,
  VORLAGE_RECHNUNG,
  type SessionUser
} from '@raporto/shared'
import { notify } from '../ui/Toasts.js'
import { speichereDatei, dateiname } from '../net/speichern.js'
import { useEinstellungen } from '../ui/hooks.js'
import { SerienMailEditor, type Serienempfaenger } from '../ui/SerienMailEditor.js'

/**
 * Betreuung – Verhältnisse pflegen, Monatsrechnungen erstellen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Ein Bildschirm und nicht zwei
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Oben der Monat, unten die Kinder. Das ist die Reihenfolge der Arbeit und
 * nicht die der Datenmodelle: Eingerichtet wird einmal, abgerechnet wird
 * zwölfmal im Jahr. Wer am Monatsende hierher kommt, soll den Monat wählen,
 * die Liste durchsehen und erstellen – drei Handgriffe, ohne zu suchen.
 *
 * Deshalb auch kein eigenes Dashboard und keine zweite Navigation. Was hier
 * entsteht, sind gewöhnliche Rechnungsentwürfe; weitergearbeitet wird unter
 * «Rechnungen», wo alle anderen auch liegen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was diese Datei NICHT entscheidet
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Keinen Preis, keinen Tarif, keine Alterskategorie, keine Frist. Alles das
 * kommt fertig vom Server – dieselbe Rechnung, die danach den Beleg schreibt.
 * Auch «darf dieser Vorschlag erzeugt werden» ist eine Antwort des Servers
 * (`erzeugbar`) und keine Prüfung hier: Eine Oberfläche, die das selbst
 * beurteilt, urteilt irgendwann anders.
 */

const ARTEN: Array<{ art: Betreuungsart; label: string }> = [
  { art: 'ganztag', label: BETREUUNGSART_LABEL.ganztag },
  { art: 'halbtag_ohne', label: BETREUUNGSART_LABEL.halbtag_ohne },
  { art: 'halbtag_mit', label: BETREUUNGSART_LABEL.halbtag_mit }
]

/**
 * Die Mengen zur Auswahl – in Viertelschritten, als Brüche beschriftet.
 *
 * Ein Zahlenfeld stand hier zuerst, und es war der falsche Baustein: Wer «2,5»
 * tippt, tippt auch «2.5», «2 1/2» oder «zweieinhalb», und drei davon kommen
 * nicht durch. Eine Liste von Vierteln kann man nicht falsch ausfüllen, und
 * sie zeigt gleich, was überhaupt vorgesehen ist.
 *
 * Bis fünf, in Vierteln: Mehr als fünf Betreuungstage hat keine Woche, und
 * Achtel vergibt keine Kita.
 */
const STUFEN: number[] = Array.from({ length: 21 }, (_, i) => i * 0.25)

const MONATE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember'
]

/** Der Vormonat – der Monat, den eine Kita am Monatsanfang abrechnet. */
function startmonat(): { jahr: number; monat: number } {
  const jetzt = new Date()
  const m = jetzt.getUTCMonth() /* 0-basiert = Vormonat in 1-basierter Zählung */
  return m === 0
    ? { jahr: jetzt.getUTCFullYear() - 1, monat: 12 }
    : { jahr: jetzt.getUTCFullYear(), monat: m }
}

interface Formular {
  id: string | null
  kindName: string
  geburtsdatum: string
  customerId: string
  beginn: string
  /**
   * Läuft die Betreuung weiter, oder endet sie an einem Tag?
   *
   * ──────────────────────────────────────────────────────────────────────────
   * Warum das eine eigene Angabe ist und nicht «Datum leer»
   * ──────────────────────────────────────────────────────────────────────────
   *
   * «Kein Enddatum» war bisher ein leeres Datumsfeld. Das ist derselbe
   * Zustand wie «noch nichts eingetragen», und ein `<input type="date">`
   * zeigt ihn in jedem Browser anders an – in Safari stand dort zuletzt der
   * heutige Tag, obwohl im Formular nichts gesetzt war. Wer das sieht,
   * glaubt, die Betreuung ende heute.
   *
   * Darum wird jetzt gefragt statt geraten: «Läuft weiter» oder «Endet am».
   * Bei «Läuft weiter» gibt es gar kein Datumsfeld – kein Feld, keine
   * Anzeige, kein Browserverhalten, kein Missverständnis.
   */
  endeModus: 'weiter' | 'endet'
  /**
   * Das Enddatum – nur bedeutsam bei `endeModus === 'endet'`.
   *
   * Es bleibt beim Umschalten auf «Läuft weiter» im Formular stehen, damit
   * ein versehentliches Umschalten den Tag nicht vernichtet. Gespeichert
   * wird es dann trotzdem nicht: Was hinausgeht, entscheidet der Modus.
   */
  ende: string
  /**
   * Ist eine Eingewöhnung vereinbart? Das Kästchen, wie beim Rabatt.
   *
   * Der Zeitraum steht in den beiden Feldern darunter. Beide bleiben beim
   * Abwählen stehen, damit ein versehentlicher Klick die Eingaben nicht
   * vernichtet – gespeichert wird nur, was mit gesetztem Häkchen gilt.
   */
  eingewoehnung: boolean
  /** Erster Tag der Eingewöhnung, frei gewählt. */
  eingewoehnungVon: string
  /** Letzter Tag der Eingewöhnung, frei gewählt. */
  eingewoehnungBis: string
  geschwisterrabatt: boolean
  fixbetrag: string
  fixbetragText: string
  hinweis: string
  /**
   * Die Teilmengen je Betreuungsart – immer mindestens ein Eintrag, auch leer.
   *
   * Eine Liste und kein einzelner Wert, weil Familien so vereinbaren: ¾ und ½
   * eines ganzen Tags. Verrechnet wird daraus EINE Position über die Summe;
   * die Teile stehen in ihrem Text. Wer nur eine Menge vereinbart hat, sieht
   * hier wie bisher genau ein Feld.
   */
  mengen: Record<Betreuungsart, string[]>
}

/** Höchstens vier Teile je Art – dieselbe Grenze wie in Migration 0054. */
const TEILE_MAX = 4

/**
 * «2026-11-25» als «25.11.2026».
 *
 * Reine Zeichenarbeit und kein `Date`: Ein Datum ohne Uhrzeit rutscht über
 * `toLocaleDateString` in der Zeitzone des Browsers in den Vortag, und ein
 * Elternteil am Tresen liest dann einen falschen Tag.
 */
function chTag(iso: string): string {
  const [j, m, t] = iso.split('-')
  return j && m && t ? `${t}.${m}.${j}` : iso
}

/**
 * Das gewählte Datum in Schweizer Schreibweise – unter dem Feld.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum das Feld selbst es nicht kann
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein `<input type="date">` zeigt sein Datum in dem Format, das der BROWSER
 * für richtig hält – das hängt an dessen Anzeigesprache und nicht an der
 * Seite. Weder ein `lang`-Attribut noch CSS noch sonst etwas aus dieser
 * Anwendung ändert daran etwas; gemessen in Chromium mit `lang="de-CH"`,
 * Seitensprache de-CH und Browsersprache de-CH: Es steht weiterhin
 * 11/25/2026 im Feld.
 *
 * Die Alternative wäre, das Datumsfeld durch ein Textfeld zu ersetzen und
 * die Eingabe selbst zu zerlegen. Dann wäre die Schreibweise unser – und der
 * Kalender weg: kein Tippen auf ein Feld, das am Telefon den Datumswähler
 * öffnet, keine Pfeiltasten, keine Prüfung des Browsers auf einen gültigen
 * Tag. Die Anordnung sagt ausdrücklich, dass die native Kalenderbedienung
 * und die Speicherung unangetastet bleiben.
 *
 * Also beides: Das Feld bleibt ein Datumsfeld, und direkt darunter steht,
 * welcher Tag gewählt ist – in `TT.MM.JJJJ`, in jedem Browser gleich. Wer
 * im Feld «11/25/2026» liest und darunter «25.11.2026», hat die Antwort an
 * der Stelle, an der die Frage entsteht.
 */
function Klartext({ wert, feld }: { wert: string; feld: string }) {
  if (wert === '') return null
  return (
    <span className="o-soft small" data-datum={feld}>
      {chTag(wert)}
    </span>
  )
}

/** Die Summe der Teilmengen einer Art. `null` heisst: nichts vereinbart. */
function summeTeile(teile: string[]): number | null {
  const zahlen = teile.map(alsMenge).filter((z): z is number => z !== null)
  if (zahlen.length === 0) return null
  return Math.round(zahlen.reduce((s, z) => s + z, 0) * 100) / 100
}

/**
 * Das leere Formular – und zwar wirklich leer.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum hier kein Datum steht
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `beginn` trug den heutigen Tag. Das war gut gemeint und falsch: Ein
 * Betreuungsverhältnis beginnt am Eintrittstag des Kindes, und der ist fast
 * nie heute – er steht im Vertrag, oft Wochen vorher oder im nächsten Monat.
 * Ein vorbelegtes Feld, das man übersieht, ist schlimmer als ein leeres, das
 * man ausfüllen muss: Es wird mitgespeichert, und danach rechnet der
 * Monatslauf ab einem Datum, das niemand gewählt hat.
 *
 * Dasselbe gilt für den Rechnungsempfänger. Er stand auf dem ersten Kunden
 * der Liste – in alphabetischer Reihenfolge, also auf einer fremden Familie.
 * Die Rechnung eines Kindes an die falschen Eltern ist der teuerste Fehler,
 * den dieses Formular machen kann.
 *
 * Was keine fachliche Vorauswahl ist und deshalb bleibt: das leere
 * Rabattkästchen (nicht angekreuzt heisst «nicht gewährt», das ist die
 * Abwesenheit einer Wahl) und die leeren Mengenfelder.
 */
const LEER: Formular = {
  id: null,
  kindName: '',
  geburtsdatum: '',
  customerId: '',
  beginn: '',
  /*
    «Läuft weiter» ist der Normalfall einer Anmeldung und keine Vorbelegung
    eines Datums: Es entsteht kein Feld und kein Tag, den jemand übersieht.
  */
  endeModus: 'weiter',
  ende: '',
  /*
    Nicht angekreuzt ist die Abwesenheit einer Wahl und keine Vorbelegung
    eines Betrags: Wer nichts ankreuzt, bekommt keine Pauschale auf der
    ersten Elternrechnung. Die beiden Daten bleiben leer – vorbelegt wird
    erst beim Ankreuzen, und dann sichtbar.
  */
  eingewoehnung: false,
  eingewoehnungVon: '',
  eingewoehnungBis: '',
  geschwisterrabatt: false,
  fixbetrag: '',
  fixbetragText: '',
  hinweis: '',
  mengen: { ganztag: [''], halbtag_ohne: [''], halbtag_mit: [''] }
}

/**
 * Die Teilmengen einer Art, wie sie gespeichert sind – für das Formular.
 *
 * Immer mindestens ein Eintrag: Ein Formular ohne Feld hätte keine Stelle, an
 * der sich die erste Menge eintragen liesse.
 */
function teileVon(
  posten: Array<{ art: Betreuungsart; tageProWoche: number }>,
  art: Betreuungsart
): string[] {
  const eigene = posten.filter((p) => p.art === art).map((p) => String(p.tageProWoche))
  return eigene.length === 0 ? [''] : eigene
}

/**
 * Die Betreuung eines Kindes als ein Satzteil – für die Stammdatenliste.
 *
 * Je Art die Summe, und dahinter die Aufteilung, wenn es eine gibt:
 * «1¼ ganze Tage pro Woche (¾ + ½)». Dieselbe Reihenfolge wie im Formular,
 * damit die Liste und die Eingabe nicht verschieden erzählen.
 */
function betreuungstext(posten: Array<{ art: Betreuungsart; tageProWoche: number }>): string {
  return ARTEN.flatMap((a) => {
    const eigene = posten.filter((p) => p.art === a.art)
    if (eigene.length === 0) return []
    const summe = Math.round(eigene.reduce((s, p) => s + p.tageProWoche, 0) * 100) / 100
    const teil = aufteilungstext(eigene.map((p) => p.tageProWoche))
    return [`${mengentext(a.art, summe)}${teil === '' ? '' : ` (${teil})`}`]
  }).join(', ')
}

/** «1234.55» oder «1234,55» als Rappen. Leer heisst: nicht gesetzt. */
function alsRappen(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (t === '') return null
  const zahl = Number(t)
  return Number.isFinite(zahl) ? Math.round(zahl * 100) : null
}

function alsMenge(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (t === '') return null
  const zahl = Number(t)
  return Number.isFinite(zahl) && zahl > 0 ? zahl : null
}

export function OfficeBetreuung({ session }: { session: SessionUser }): JSX.Element {
  const [verhaeltnisse, setVerhaeltnisse] = useState<Betreuungsverhaeltnis[]>([])
  const [kunden, setKunden] = useState<BueroKunde[]>([])
  const [monat, setMonat] = useState(startmonat())
  const [vorschau, setVorschau] = useState<Betreuungsmonat | null>(null)
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  const [formular, setFormular] = useState<Formular | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  /*
    Ob der gemeinsame Mail-Editor offen ist.

    Er steht zwischen dem Knopf und dem Versand – vorher ging die Serie ohne
    jede Zwischenstufe hinaus.
  */
  const [serienmail, setSerienmail] = useState(false)
  /* Der Firmenname für `{{firmenname}}` in der Vorschau. */
  const { firma } = useEinstellungen()
  const [fehler, setFehler] = useState<string | null>(null)
  /**
   * Die eine Bestätigung vor der Freigabe.
   *
   * Eine Freigabe vergibt Nummern und druckt PDFs; danach ist jede dieser
   * Rechnungen ein Beleg und wird nur noch storniert, nicht mehr geändert.
   * Deshalb EINE Frage mit der Anzahl – und keine je Kind: Dreissig
   * Bestätigungen hintereinander liest niemand, die klickt man weg.
   */
  const [freigabeFrage, setFreigabeFrage] = useState(false)
  /**
   * Die Bestätigung vor dem Erstellen – mit der Liste, die entsteht.
   *
   * Vorher ging der Klick direkt in den Lauf. Bei einer Vorauswahl über alle
   * Kinder war das der Weg zur Sammelrechnung; ohne Vorauswahl ist es
   * harmloser, aber die Frage gehört trotzdem davor: Eine Rechnung ist eine
   * Forderung an eine Familie, und wer sie auslöst, soll vorher sehen, an
   * welche und über wie viel.
   */
  const [erstellenFrage, setErstellenFrage] = useState(false)
  /**
   * Die Rechnung, die ersetzt werden soll – oder `null`.
   *
   * Das Ersetzen hebt einen Beleg auf. Es fragt deshalb einzeln, nennt den
   * Beleg beim Namen und sagt, was mit ihm geschieht.
   */
  const [korrektur, setKorrektur] = useState<Betreuungsvorschlag | null>(null)
  /*
    Wie weit der Lauf ist.

    Die Entwürfe entstehen einer nach dem anderen, und bei zwanzig Kindern
    dauert das ein paar Sekunden. «Wird erstellt …» allein lässt offen, ob
    überhaupt etwas passiert – «7 von 20» nicht.
  */
  const [fortschritt, setFortschritt] = useState<{ fertig: number; gesamt: number } | null>(null)

  const laden = useCallback(async () => {
    try {
      const [v, k] = await Promise.all([
        api.betreuungVerhaeltnisse(),
        /* Dieselbe Kundenliste, die der Kundenbildschirm zeigt – keine zweite. */
        api.officeCustomers('aktiv')
      ])
      setVerhaeltnisse(v.verhaeltnisse)
      setKunden(k.items)
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler')
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  const ladeVorschau = useCallback(async () => {
    setLaeuft('vorschau')
    try {
      const v = await api.betreuungMonat(monat.jahr, monat.monat)
      setVorschau(v)
      /*
        ══════════════════════════════════════════════════════════════════
        Nichts ist vorausgewählt – und das ist die Korrektur
        ══════════════════════════════════════════════════════════════════

        Hier stand: alles ankreuzen, was geht. Die Begründung war die
        Bequemlichkeit am Monatsende, und für den Monatsabschluss stimmte
        sie auch. Für den Fall, der gemeldet wurde, stimmte sie nicht:

        Ein Kind wird im laufenden Monat nachgetragen. Die Betreiberin
        öffnet die Abrechnung, um GENAU DESSEN Rechnung zu erstellen – und
        findet alle dreissig Kinder angekreuzt vor. Ein Klick auf den
        grossen Knopf, und es entstehen dreissig Entwürfe statt einem.
        Wer das nicht bemerkt, gibt sie frei; danach stehen Forderungen im
        Raum, die niemand stellen wollte.

        Eine Vorauswahl ist eine Entscheidung, die niemand getroffen hat.
        Deshalb ist die Liste jetzt leer, und das Anhaken ist die Wahl.
        Die Bequemlichkeit bleibt – als Knopf, der sagt, was er tut:
        «Alle noch offenen auswählen».
      */
      setGewaehlt(new Set())
      setFehler(null)
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Unbekannter Fehler'
      setFehler(text)
      notify.error(text)
    } finally {
      setLaeuft(null)
    }
  }, [monat])

  /*
    Die Vorschau lädt von selbst – beim Öffnen und bei jedem Monatswechsel.

    ══════════════════════════════════════════════════════════════════════════
    Das war die Ursache für «geht erst beim dritten Versuch»
    ══════════════════════════════════════════════════════════════════════════

    `ladeVorschau` hing allein am Knopf «Vorschau laden». Daraus folgten zwei
    Zustände, in denen ein Klick auf «Rechnungsentwürfe erstellen» nichts tat:

      Beim Öffnen stand noch keine Vorschau da. Der Bereich zeigte nur den
      Ladeknopf – wer den Monat abrechnen wollte, musste erst einen Knopf
      finden, der nach Technik aussieht und nicht nach Arbeit.

      Nach einem Monatswechsel stand die Vorschau des VORIGEN Monats da. Die
      Liste zeigte dessen Kinder, der Erstellen-Lauf schickte aber den neu
      gewählten Monat mit – der Server wies ab, und die Meldung passte zu
      nichts, was auf dem Schirm stand.

    Beides ist weg: Die Vorschau folgt dem gewählten Monat. Der Ladeknopf
    bleibt für das bewusste Nachladen, aber niemand muss ihn mehr finden.
  */
  useEffect(() => {
    void ladeVorschau()
  }, [ladeVorschau])

  /** Die ausgewählten Entwürfe erstellen – einer nach dem anderen. */
  async function erstellen(): Promise<void> {
    /*
      Ein Lauf zur Zeit, und das steht hier und nicht nur am `disabled`.

      Die Schaltfläche ist während des Laufs gesperrt; ein Doppelklick im
      selben Augenblick käme aber vor dem erneuten Zeichnen durch. Der Riegel
      im Code ist der, der hält.
    */
    if (laeuft !== null) return
    if (!vorschau) return
    setErstellenFrage(false)

    /*
      Der Monat kommt aus der VORSCHAU, nicht aus der Auswahlliste.

      Das ist die eigentliche Korrektur: Beides kann auseinanderlaufen, und
      dann entstehen Entwürfe für einen Monat, dessen Kinder gerade nicht auf
      dem Schirm stehen. Eine Quelle, und zwar die, die der Mensch sieht.
    */
    const ziel = vorschau.monat
    const ziele = vorschau.vorschlaege.filter(
      (v) => v.erzeugbar && gewaehlt.has(v.verhaeltnisId)
    )
    if (ziele.length === 0) {
      /*
        Kein stilles Nichts.

        Hier stand `return` ohne Wort. Wer klickt und nichts sieht, klickt
        wieder – und genau das wurde als «funktioniert erst nach mehrmaligem
        Auslösen» gemeldet.
      */
      const schonDa = vorschau.vorschlaege.filter((v) => v.vorhanden !== null).length
      notify.warn(
        gewaehlt.size === 0
          ? 'Kein Kind ausgewählt. Die Kästchen links wählen, welche Rechnungen entstehen.'
          : schonDa > 0
            ? 'Für die ausgewählten Kinder besteht in diesem Monat schon eine Rechnung.'
            : 'Für die ausgewählten Kinder lässt sich in diesem Monat keine Rechnung erstellen.'
      )
      return
    }
    setLaeuft('erstellen')
    setFortschritt({ fertig: 0, gesamt: ziele.length })
    let fertig = 0
    const misslungen: string[] = []
    /*
      Der Reihe nach und nicht alle gleichzeitig.

      Jede Rechnung bekommt ihre eigene Aktionskennung, und ein Fehler bei
      einem Kind soll die anderen nicht mitnehmen. Nebenläufig wäre es
      schneller und die Fehlermeldung unzuordenbar.
    */
    for (const z of ziele) {
      try {
        await api.betreuungEntwurf(z.verhaeltnisId, ziel.jahr, ziel.monat, uuidv7())
        fertig++
        setFortschritt({ fertig, gesamt: ziele.length })
      } catch (e) {
        misslungen.push(`${z.kindName}: ${e instanceof Error ? e.message : 'Fehler'}`)
      }
    }
    setLaeuft(null)
    setFortschritt(null)
    if (fertig > 0) {
      notify.ok(
        fertig === 1
          ? '1 Rechnungsentwurf erstellt. Er liegt unter «Rechnungen».'
          : `${fertig} Rechnungsentwürfe erstellt. Sie liegen unter «Rechnungen».`
      )
    }
    for (const m of misslungen) notify.warn(m)
    await ladeVorschau()
  }

  /**
   * Alle vorbereiteten Rechnungen des Monats freigeben.
   *
   * Eine Anfrage, ein Ergebnis je Rechnung. Was scheitert, scheitert allein –
   * und steht danach als Meldung da, nicht als stummer Fehlversuch. Der
   * Server bildet je Beleg eine feste Aktionskennung aus dieser hier, deshalb
   * erzeugt ein zweiter Klick keine zweite Nummer.
   */
  async function sammelAbschluss(): Promise<void> {
    /* Ein Lauf zur Zeit – siehe `erstellen`. */
    if (laeuft !== null) return
    if (!vorschau || entwuerfe.length === 0) return
    setFreigabeFrage(false)
    setLaeuft('abschluss')
    try {
      const a = await api.betreuungMonatAbschluss(
        vorschau.monat.jahr,
        vorschau.monat.monat,
        entwuerfe,
        uuidv7()
      )
      if (a.abgeschlossen > 0) {
        notify.ok(
          a.abgeschlossen === 1
            ? '1 Rechnung freigegeben. Sie liegt unter «Rechnungen».'
            : `${a.abgeschlossen} Rechnungen freigegeben. Sie liegen unter «Rechnungen».`
        )
      }
      if (a.warSchon > 0) {
        notify.ok(
          a.warSchon === 1
            ? '1 Rechnung war bereits freigegeben.'
            : `${a.warSchon} Rechnungen waren bereits freigegeben.`
        )
      }
      /* Jede gescheiterte Rechnung einzeln – eine Sammelmeldung sagt nicht, welche. */
      for (const e of a.ergebnisse) {
        if (e.ergebnis === 'fehler') notify.warn(e.meldung ?? 'Abschluss nicht möglich.')
        else if (e.meldung) notify.warn(e.meldung)
      }
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(null)
      await ladeVorschau()
    }
  }

  /**
   * Den Serienversand vorbereiten – senden tut erst der Dialog.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * Warum zwei Schritte und nicht einer
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Vorher löste dieser Knopf den Versand an alle Familien unmittelbar aus:
   * ein Klick, dreissig Mails, Text aus dem Serverquelltext. Das war der
   * letzte Weg im ganzen Programm, auf dem eine E-Mail ohne Editor hinausging.
   *
   * Jetzt prüft dieser Schritt nur, OB gesendet werden kann, und öffnet dann
   * den gemeinsamen Editor. Dort steht der Text, dort steht die Vorschau je
   * Familie, und dort steht der Knopf mit der Anzahl darauf.
   */
  async function versandVorbereiten(): Promise<void> {
    if (laeuft !== null) return
    if (!vorschau || abgeschlossene.length === 0) return

    /*
      Erst nachsehen, ob der Versand an Eltern überhaupt möglich ist.

      Er läuft über den eigenen Mailserver des Betriebs. Ohne hinterlegten
      SMTP-Zugang antwortet jede einzelne Anfrage mit demselben 409 – dann
      dreissig Fehlversuche zu erzeugen wäre die unfreundlichste Art, es zu
      sagen. Gefragt wird an EINER Rechnung; die Antwort gilt für den Betrieb.
    */
    const erste = abgeschlossene[0].vorhanden as { documentId: string }
    setLaeuft('versand')
    try {
      const wege = await api.versandwege(erste.documentId)
      if (!wege.kundenversand.moeglich) {
        notify.warn(
          `${wege.kundenversand.grund ?? 'Der Versand an Eltern ist nicht möglich.'} ` +
            'Bis dahin alle Rechnungen als ZIP herunterladen und von Hand verschicken.'
        )
        return
      }
      setSerienmail(true)
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(null)
    }
  }

  /**
   * Die abgeschlossenen Rechnungen des Monats an die Eltern senden.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * Warum hier geschleift wird und keine Sammelroute entsteht
   * ══════════════════════════════════════════════════════════════════════════
   *
   * `POST /documents/:id/versand` ist der geprüfte Versandweg: Er nimmt KEINE
   * Empfängeradresse an, kennt nur drei feste Wege, hat seinen eigenen
   * Schutz gegen den zweiten Versand, seine Ratenbremse und sein Protokoll je
   * Beleg. Eine zweite Sammelroute daneben müsste all das nachbauen – und
   * beim nächsten Fehler würde nur eine der beiden korrigiert. Ein offenes
   * Relais entsteht genau so.
   *
   * Deshalb ruft die Oberfläche denselben Weg je Rechnung auf, nacheinander.
   * Was scheitert, scheitert allein und steht danach als eigene Meldung da.
   *
   * Betreff und Text gehen MIT Platzhaltern hinaus. Gefüllt wird je Beleg auf
   * dem Server, aus den Daten dieses Belegs – ein Text für dreissig Familien,
   * dreissig verschiedene Rechnungsnummern darin.
   */
  async function sammelVersand(betreff: string, text: string): Promise<void> {
    if (laeuft !== null) return
    if (!vorschau || abgeschlossene.length === 0) return
    setLaeuft('versand')
    try {
      let gesendet = 0
      const misslungen: string[] = []
      for (const v of abgeschlossene) {
        const b = v.vorhanden as { documentId: string; nummer: string | null }
        try {
          await api.versenden(b.documentId, 'kunde', uuidv7(), { betreff, text })
          gesendet++
        } catch (e) {
          misslungen.push(
            `${b.nummer ?? v.kindName}: ${e instanceof Error ? e.message : 'Versand fehlgeschlagen'}`
          )
        }
      }
      if (gesendet > 0) {
        notify.ok(
          gesendet === 1 ? '1 Rechnung versendet.' : `${gesendet} Rechnungen versendet.`
        )
      }
      for (const m of misslungen) notify.warn(m)
      /*
        Der Dialog schliesst nur, wenn ALLES durch ist.

        Bleibt einer liegen, bleibt der Text stehen: Wer dreissig Mails
        abgeschickt hat und bei zwei einen Mailserverfehler bekommt, soll den
        Text nicht neu schreiben müssen, um die zwei nachzuholen. Die bereits
        versendeten sind dabei geschützt – der Server lässt einen zweiten
        Versand desselben Belegs nicht durch.
      */
      if (misslungen.length === 0) setSerienmail(false)
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(null)
      await ladeVorschau()
    }
  }

  async function speichern(): Promise<void> {
    if (!formular) return
    /*
      Je Teilmenge ein Posten, in der eingegebenen Reihenfolge.

      Leere Felder fallen weg – ein angefangenes, nicht gefülltes Feld ist
      keine Vereinbarung über null Tage.
    */
    const posten = ARTEN.flatMap((a) =>
      formular.mengen[a.art].flatMap((text) => {
        const menge = alsMenge(text)
        return menge === null ? [] : [{ art: a.art, tageProWoche: menge }]
      })
    )
    const eingabe: Betreuungseingabe = {
      kindName: formular.kindName.trim(),
      geburtsdatum: formular.geburtsdatum,
      customerId: formular.customerId,
      beginn: formular.beginn,
      /*
        Der Modus entscheidet, nicht das Feld.

        «Läuft weiter» sendet `null` – ohne Rücksicht darauf, welcher Tag im
        Formular noch steht oder was irgendein Datumsfeld anzeigt. Erst
        dieses Speichern ändert den gespeicherten Stand; bis dahin bleibt am
        Verhältnis, was dort stand.
      */
      ende: formular.endeModus === 'weiter' || formular.ende.trim() === '' ? null : formular.ende,
      eingewoehnung: formular.eingewoehnung,
      /*
        Ohne Häkchen gehen die Daten nicht hinaus – sonst stünde am
        Verhältnis ein Zeitraum zu einer Eingewöhnung, die nicht vereinbart
        ist. Im Formular bleiben sie stehen.
      */
      eingewoehnungVon: formular.eingewoehnung ? formular.eingewoehnungVon || null : null,
      eingewoehnungBis: formular.eingewoehnung ? formular.eingewoehnungBis || null : null,
      geschwisterrabatt: formular.geschwisterrabatt,
      fixbetragRp: alsRappen(formular.fixbetrag),
      fixbetragText: formular.fixbetragText.trim() || null,
      hinweis: formular.hinweis.trim() || null,
      posten
    }

    setLaeuft('speichern')
    try {
      if (formular.id) {
        await api.betreuungAendern(formular.id, eingabe, uuidv7())
        notify.ok(`${eingabe.kindName}: gespeichert.`)
      } else {
        await api.betreuungAnlegen(eingabe, uuidv7())
        notify.ok(`${eingabe.kindName}: angelegt.`)
      }
      setFormular(null)
      await laden()
      if (vorschau) await ladeVorschau()
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Unbekannter Fehler'
      setFehler(text)
      notify.error(text)
    } finally {
      setLaeuft(null)
    }
  }

  /**
   * Eine bestehende Rechnung ersetzen – nach ausdrücklicher Bestätigung.
   *
   * Storno und Ersatz macht der Server in EINEM Vorgang; scheitert der
   * Ersatz, bleibt kein Storno zurück. Hier steht nur die Frage davor und
   * die Rückmeldung danach.
   */
  async function ersetzen(v: Betreuungsvorschlag): Promise<void> {
    if (laeuft !== null || !vorschau) return
    setKorrektur(null)
    setLaeuft(`ersetzen-${v.verhaeltnisId}`)
    try {
      const r = await api.betreuungErsetzen(
        v.verhaeltnisId,
        vorschau.monat.jahr,
        vorschau.monat.monat,
        uuidv7()
      )
      notify.ok(
        r.ersetzt.weg === 'storniert'
          ? `${v.kindName}: Rechnung ${r.ersetzt.nummer ?? ''} storniert, neuer Entwurf über ` +
            `${formatChf(r.nettoRp)} erstellt.`
          : `${v.kindName}: Entwurf verworfen, neuer Entwurf über ${formatChf(r.nettoRp)} erstellt.`
      )
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(null)
      await ladeVorschau()
    }
  }

  async function stilllegen(v: Betreuungsverhaeltnis): Promise<void> {
    setLaeuft(`still-${v.id}`)
    try {
      await api.betreuungStilllegen(v.id, uuidv7())
      notify.ok(`${v.kindName}: Betreuung beendet.`)
      await laden()
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(null)
    }
  }

  /*
    Hier stand `freigeben()`: ein `window.prompt` nach jedem Storno, Kind für
    Kind, mit abgetipptem Grund. Es ist weg, und zwar ersatzlos – eine
    stornierte Rechnung gibt ihren Monat jetzt von selbst wieder frei (siehe
    `services/betreuung.ts`). Der Grund steht im Storno, wo er hingehört, und
    die Korrekturrechnung entsteht über denselben Weg wie jede andere: anhaken
    und erstellen.
  */

  const wartet = (k: string): boolean => laeuft === k
  const irgendwas = laeuft !== null

  /*
    Die Entwürfe dieses Monats, die ANGEHAKT sind – das sind die, die
    freigegeben werden.

    Hier stand: alle Entwürfe des Monats, ohne Auswahl, mit der Begründung,
    wer einzelne ausnehmen wolle, möge sie im Belegdetail abschliessen. Das
    hat sich in der Kita nicht bewährt: Dreissig Kinder, zwei davon mit einer
    offenen Frage an die Eltern – und die Betreiberin musste alles oder nichts
    freigeben oder 28 Belege einzeln öffnen.

    Dieselben Kästchen wie für das Erstellen, und jede Aktion nimmt daraus,
    was zu ihr passt. Was nicht angehakt ist, bleibt Entwurf.
  */
  const entwuerfe = useMemo(
    () =>
      (vorschau?.vorschlaege ?? [])
        .filter(
          (v) =>
            v.vorhanden && v.vorhanden.status === 'entwurf' && gewaehlt.has(v.verhaeltnisId)
        )
        .map((v) => (v.vorhanden as { documentId: string }).documentId),
    [vorschau, gewaehlt]
  )

  /** Die angehakten Kinder, für die eine Rechnung ENTSTEHEN kann. */
  const neuGewaehlt = useMemo(
    () =>
      (vorschau?.vorschlaege ?? []).filter((v) => v.erzeugbar && gewaehlt.has(v.verhaeltnisId)),
    [vorschau, gewaehlt]
  )

  /** Wie viele Entwürfe der Monat überhaupt trägt – für den Satz «x von y». */
  const entwuerfeGesamt = useMemo(
    () =>
      (vorschau?.vorschlaege ?? []).filter((v) => v.vorhanden?.status === 'entwurf').length,
    [vorschau]
  )

  /*
    Die abgeschlossenen Rechnungen des Monats.

    Sie sind die Grundlage für beides, was danach kommt: das ZIP und den
    Versand. Ein Entwurf gehört in keines von beiden – er hat keine Nummer
    und kann sich noch ändern.
  */
  const abgeschlossene = useMemo(
    () =>
      (vorschau?.vorschlaege ?? []).filter(
        (v) => v.vorhanden && v.vorhanden.nummer !== null && v.vorhanden.status !== 'storniert'
      ),
    [vorschau]
  )

  /*
    Die Empfänger für den gemeinsamen Editor.

    Dieselbe Liste, die auch versendet wird – aus `abgeschlossene` abgeleitet
    und nicht neu zusammengestellt, damit die Vorschau nicht eine andere Menge
    zeigt als der Knopf verschickt. Ohne Nummer käme ein Beleg hier nicht an;
    `abgeschlossene` lässt nur nummerierte durch.
  */
  const serienempfaenger = useMemo<Serienempfaenger[]>(
    () =>
      abgeschlossene.map((v) => ({
        id: (v.vorhanden as { documentId: string }).documentId,
        anzeige: v.kindName,
        nummer: (v.vorhanden as { nummer: string | null }).nummer ?? '',
        kundenname: v.kundenname
      })),
    [abgeschlossene]
  )

  /**
   * Die Kinder, für die in diesem Monat noch eine Rechnung FEHLT.
   *
   * Genau das, was «Alle noch offenen auswählen» anhakt – und nichts
   * daneben: kein Kind mit bestehender Rechnung, kein Kind ohne Position,
   * keines mit fehlendem Tarif. Der Server hat das schon entschieden
   * (`erzeugbar`); diese Liste liest es nur ab.
   */
  const nochOffen = useMemo(
    () => (vorschau?.vorschlaege ?? []).filter((v) => v.erzeugbar),
    [vorschau]
  )

  const summe = useMemo(
    () =>
      (vorschau?.vorschlaege ?? [])
        .filter((v) => v.erzeugbar && gewaehlt.has(v.verhaeltnisId))
        .reduce((s, v) => s + v.nettoRp, 0),
    [vorschau, gewaehlt]
  )

  const jahre = useMemo(() => {
    const j = new Date().getUTCFullYear()
    return [j - 1, j, j + 1]
  }, [])

  return (
    <div className="o-blatt">
      {fehler && <p className="o-warn-text">{fehler}</p>}

      {/* ── Monatsrechnungen ───────────────────────────────────────────── */}
      <section className="o-card" id="betreuung-monat">
        <div className="o-card-head">
          <h2>Monatsrechnungen</h2>
          <span className="o-soft small">
            Monat wählen, Liste prüfen, Entwürfe erstellen. Abgeschlossen und verschickt wird
            unter «Rechnungen».
          </span>
        </div>

        <div className="o-filter">
          <label htmlFor="betreuung-jahr">Jahr</label>
          <select
            id="betreuung-jahr"
            value={monat.jahr}
            onChange={(e) => setMonat({ ...monat, jahr: Number(e.target.value) })}
          >
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
          <label htmlFor="betreuung-monatwahl">Monat</label>
          <select
            id="betreuung-monatwahl"
            value={monat.monat}
            onChange={(e) => setMonat({ ...monat, monat: Number(e.target.value) })}
          >
            {MONATE.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="o-btn o-btn-primary"
            id="betreuung-vorschau"
            disabled={irgendwas}
            onClick={() => void ladeVorschau()}
          >
            {wartet('vorschau') ? 'Wird gerechnet …' : 'Vorschau laden'}
          </button>
        </div>

        {vorschau && vorschau.vorschlaege.length === 0 && (
          <p className="o-leer">
            Im {vorschau.bezeichnung} ist kein Betreuungsverhältnis aktiv.
          </p>
        )}

        {vorschau && vorschau.vorschlaege.length > 0 && (
          <>
            {/*
              Die Zahlen des Monats, bevor die Liste kommt.

              Sie stehen oben, weil die Frage beim Monatsende nicht «welche
              Kinder» ist, sondern «was ist noch zu tun». Wer das in zwei
              Sekunden sieht, braucht die Liste darunter nur für die
              Ausnahmen. Gezählt hat der Server – dieselbe Quelle wie die
              Zeilen, siehe `Monatszahlen` in services/betreuung.ts.
            */}
            <div className="o-kennzahlen" id="betreuung-zahlen">
              <div className="o-kennzahl">
                <dt>Vorgesehen</dt>
                <dd>{vorschau.zahlen.vorgesehen}</dd>
              </div>
              <div className="o-kennzahl">
                <dt>Gesamtbetrag</dt>
                <dd className="o-mono">{formatChf(vorschau.zahlen.gesamtRp)}</dd>
              </div>
              <div className="o-kennzahl">
                <dt>Vorzubereiten</dt>
                <dd>{vorschau.zahlen.offenVorzubereiten}</dd>
              </div>
              <div className="o-kennzahl">
                <dt>Bereit zur Freigabe</dt>
                <dd>{vorschau.zahlen.bereitZurFreigabe}</dd>
              </div>
              <div className="o-kennzahl">
                <dt>Abgeschlossen</dt>
                <dd>{vorschau.zahlen.abgeschlossen}</dd>
              </div>
              {/*
                Nur diese Kachel trägt den Streifen – `alarm` ist die
                bestehende Form des Designsystems für «hier stimmt etwas
                nicht». Auf allen Kacheln wäre er Dekoration, und sie
                erscheint ohnehin nur, wenn es etwas zu sehen gibt.
              */}
              {vorschau.zahlen.brauchtPruefung > 0 && (
                <div className="o-kennzahl alarm">
                  <dt>Braucht einen Blick</dt>
                  <dd>{vorschau.zahlen.brauchtPruefung}</dd>
                </div>
              )}
              {vorschau.zahlen.abweichend > 0 && (
                <div className="o-kennzahl alarm">
                  <dt>Entwurf veraltet</dt>
                  <dd>{vorschau.zahlen.abweichend}</dd>
                </div>
              )}
              {vorschau.zahlen.storniert > 0 && (
                <div className="o-kennzahl">
                  <dt>Storniert</dt>
                  <dd>{vorschau.zahlen.storniert}</dd>
                </div>
              )}
            </div>

            {/*
              ══════════════════════════════════════════════════════════════
              Die Auswahlhilfen – statt einer Vorauswahl
              ══════════════════════════════════════════════════════════════

              Angekreuzt wird nichts von selbst (siehe `ladeVorschau`). Damit
              der Monatsabschluss trotzdem nicht dreissig Klicks kostet,
              steht hier ein Knopf, der sagt, was er anhakt: die Kinder, für
              die in diesem Monat noch eine Rechnung fehlt. Nicht «alle» –
              ein Kind mit bestehender Rechnung gehört nicht dazu, und ein
              Knopf, der mehr anhakt als sein Name sagt, ist genau die
              Falle, die abgeschafft wurde.
            */}
            <div className="office-actions" id="betreuung-auswahlhilfen">
              <button
                type="button"
                className="o-btn"
                id="betreuung-alle-offenen"
                disabled={irgendwas || nochOffen.length === 0}
                onClick={() => setGewaehlt(new Set(nochOffen.map((v) => v.verhaeltnisId)))}
              >
                {nochOffen.length === 1
                  ? '1 noch offene auswählen'
                  : `Alle ${nochOffen.length} noch offenen auswählen`}
              </button>
              {entwuerfeGesamt > 0 && (
                <button
                  type="button"
                  className="o-btn o-btn-ghost"
                  id="betreuung-alle-entwuerfe"
                  disabled={irgendwas}
                  onClick={() =>
                    setGewaehlt(
                      new Set(
                        (vorschau.vorschlaege ?? [])
                          .filter((v) => v.vorhanden?.status === 'entwurf')
                          .map((v) => v.verhaeltnisId)
                      )
                    )
                  }
                >
                  {entwuerfeGesamt === 1
                    ? '1 wartenden Entwurf auswählen'
                    : `Alle ${entwuerfeGesamt} wartenden Entwürfe auswählen`}
                </button>
              )}
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="betreuung-auswahl-leeren"
                disabled={irgendwas || gewaehlt.size === 0}
                onClick={() => setGewaehlt(new Set())}
              >
                Auswahl aufheben
              </button>
            </div>

            {/*
              Der Rollbehälter um die Tabelle, nicht AN ihr.

              Hier stand `className="o-tabelle"` am `<table>` selbst. `.o-tabelle`
              trägt aber `overflow-x: auto`, und das wirkt auf einem
              Tabellenelement nicht – gebraucht wird ein Block darum. Die Folge
              war die gemeldete: `.office-main` schneidet waagrecht ab
              (`overflow-x: clip`), und bei sieben Spalten verschwand die
              rechte – genau die mit «Bearbeiten». Eine Aktion, die nicht
              erreichbar ist, gibt es nicht.

              So macht es jede andere Liste im Büro: div.o-tabelle > table.o-data.
            */}
            <div className="o-tabelle">
            <table className="o-data" id="betreuung-liste">
              <thead>
                <tr>
                  <th />
                  <th>Kind</th>
                  <th>Rechnungsempfänger</th>
                  <th>Betreuung</th>
                  <th>Tarif</th>
                  <th className="o-num">Betrag</th>
                  <th>Stand</th>
                  <th>Korrektur</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.vorschlaege.map((v) => (
                  <tr key={v.verhaeltnisId} data-erzeugbar={v.erzeugbar ? 'ja' : 'nein'}>
                    <td>
                      <input
                        type="checkbox"
                        id={`betreuung-wahl-${v.verhaeltnisId}`}
                        aria-label={`${v.kindName} abrechnen`}
                        autoComplete="off"
                        checked={gewaehlt.has(v.verhaeltnisId)}
                        /*
                          Anhaken lässt sich, womit sich etwas tun lässt: ein
                          Kind, für das eine Rechnung entstehen kann – oder
                          eines, dessen Entwurf auf die Freigabe wartet. Beide
                          Aktionen unten nehmen aus derselben Auswahl, was zu
                          ihnen passt; eine zweite Auswahlspalte für die
                          Freigabe wäre dieselbe Verwechslungsquelle, nur
                          doppelt.
                        */
                        disabled={
                          (!v.erzeugbar && v.vorhanden?.status !== 'entwurf') || irgendwas
                        }
                        onChange={(e) => {
                          const neu = new Set(gewaehlt)
                          if (e.target.checked) neu.add(v.verhaeltnisId)
                          else neu.delete(v.verhaeltnisId)
                          setGewaehlt(neu)
                        }}
                      />
                    </td>
                    <td>{v.kindName}</td>
                    <td>{v.kundenname}</td>
                    <td data-betreuung={v.verhaeltnisId}>
                      {/*
                        Die Mengen als Brueche – aus den berechneten Positionen
                        und nicht aus einer zweiten Quelle. Abzuege (der
                        Geschwisterrabatt) stehen nicht dabei: Sie sind keine
                        Betreuungsmenge, sondern ein Rabatt auf sie.
                      */}
                      {v.positionen
                        .filter((p) => p.einzelpreisRp > 0 && p.einheit.startsWith('Tag/'))
                        .map((p) => alsBruch(p.menge))
                        .join(' + ') || '–'}
                      {/*
                        Ein Monat, der vollständig in der Eingewöhnung liegt,
                        hat keine Betreuungsmenge – und damit stand in dieser
                        Spalte ein «–» neben einem Betrag von 500.00. Das sieht
                        wie ein Fehler aus. Der Satz sagt, dass es keiner ist.
                      */}
                      {v.nurEingewoehnung && (
                        <span className="o-soft small" data-eingewoehnung={v.verhaeltnisId}>
                          {' '}
                          Eingewöhnung – keine reguläre Pauschale
                        </span>
                      )}
                      {/*
                        Und im ersten regulären Monat: warum der Betrag
                        kleiner ist als die Pauschale. Ohne diesen Satz ruft
                        die Betreiberin an, weil der Dezember nicht stimmt.
                      */}
                      {v.anteiligAb && (
                        <span className="o-soft small" data-anteilig={v.verhaeltnisId}>
                          {' '}
                          anteilig ab {chTag(v.anteiligAb)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="o-badge o-badge-neutral">
                        {v.kategorie === 'baby' ? 'Baby' : 'Kleinkind'}
                      </span>
                    </td>
                    {/*
                      Der Betrag des ENTWURFS, wenn es einen gibt.

                      Hier stand immer die frisch gerechnete Summe. Bei einem
                      Entwurf vom 25., an dem danach etwas geändert wurde,
                      zeigte die Liste damit eine andere Zahl als die, die auf
                      die Rechnung geht. Gezeigt wird jetzt, was gilt – und
                      wenn beides auseinanderläuft, steht es daneben.
                    */}
                    <td className="o-num o-mono">
                      {formatChf(v.vorhanden ? v.vorhanden.nettoRp : v.nettoRp)}
                      {v.abweichungRp !== null && v.abweichungRp !== 0 && (
                        <span className="o-soft small" data-abweichung={v.verhaeltnisId}>
                          {' '}
                          (neu gerechnet {formatChf(v.nettoRp)})
                        </span>
                      )}
                    </td>
                    {/*
                      ══════════════════════════════════════════════════════
                      Der Stand – und was er über GELD sagt
                      ══════════════════════════════════════════════════════

                      Hier stand «bereit» oder der Grund, warum nicht. Was
                      fehlte, war der Unterschied zwischen einem Entwurf,
                      einer offenen Rechnung und einer bezahlten. Genau den
                      braucht man, bevor man etwas ersetzt: Ein Entwurf wird
                      verworfen, eine offene Rechnung storniert – und eine
                      bezahlte gar nicht.

                      Gerechnet hat den Zahlungsstand der Server, mit
                      derselben Funktion wie das Kundenblatt und die
                      Zahlungsübersicht. Dieser Bildschirm legt nichts aus.
                    */}
                    <td data-stand={v.verhaeltnisId}>
                      {v.vorhanden === null ? (
                        <span className="o-soft small">
                          {v.erzeugbar ? 'bereit' : v.grund}
                        </span>
                      ) : (
                        <>
                          <span
                            className={
                              v.vorhanden.zahlungsstatus === 'bezahlt'
                                ? 'o-badge o-badge-ok'
                                : v.vorhanden.status === 'storniert'
                                  ? 'o-badge o-badge-neutral'
                                  : 'o-badge o-badge-neutral'
                            }
                            data-zahlstand={v.verhaeltnisId}
                          >
                            {v.vorhanden.status === 'storniert'
                              ? 'storniert'
                              : v.vorhanden.nummer === null
                                ? 'Entwurf'
                                : v.vorhanden.zahlungsstatus === 'bezahlt'
                                  ? 'bezahlt'
                                  : v.vorhanden.zahlungsstatus === 'teilbezahlt'
                                    ? 'teilbezahlt'
                                    : v.vorhanden.zahlungsstatus === 'ueberbezahlt'
                                      ? 'überbezahlt'
                                      : 'offen'}
                          </span>
                          <span className="o-soft small">
                            {v.vorhanden.nummer ? ` Rechnung ${v.vorhanden.nummer}` : ''}
                            {v.vorhanden.bezahltRp > 0 &&
                              v.vorhanden.zahlungsstatus !== 'bezahlt' &&
                              ` · ${formatChf(v.vorhanden.bezahltRp)} eingegangen`}
                            {/*
                              Der Faden zwischen Storno und Ersatz, in beide
                              Richtungen lesbar – aus der Verknüpfung am
                              Beleg und nicht aus einem Notiztext.
                            */}
                            {v.vorhanden.ersetztDurch &&
                              ` · ersetzt durch ${
                                v.vorhanden.ersetztDurch.nummer ?? 'den neuen Entwurf'
                              }`}
                            {v.vorhanden.ersetztNummer &&
                              ` · ersetzt Rechnung ${v.vorhanden.ersetztNummer}`}
                          </span>
                          {v.abweichungRp !== null && v.abweichungRp !== 0 && (
                            <span className="o-soft small">
                              {' '}
                              Seit dem Vorbereiten wurde etwas geändert – der Entwurf rechnet
                              noch mit dem alten Stand.
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    {/*
                      Die Korrektur steht in der Zeile, zu der sie gehört –
                      und nur dort, wo sie etwas bewirkt. Ein Knopf neben
                      einem Kind ohne Rechnung wäre ein Angebot ohne
                      Gegenstand; einer neben einer bezahlten Rechnung wäre
                      eine Einladung in die Buchhaltung.
                    */}
                    <td>
                      {v.vorhanden && v.vorhanden.status !== 'storniert' && (
                        <button
                          type="button"
                          className="o-btn o-btn-ghost"
                          id={`betreuung-korrigieren-${v.verhaeltnisId}`}
                          disabled={irgendwas || !v.korrigierbar}
                          title={v.korrigierbar ? undefined : v.korrekturGrund}
                          onClick={() => setKorrektur(v)}
                        >
                          {wartet(`ersetzen-${v.verhaeltnisId}`)
                            ? 'Wird ersetzt …'
                            : 'Korrigieren'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/*
              ══════════════════════════════════════════════════════════════
              Vor dem Erstellen: zeigen, was entsteht
              ══════════════════════════════════════════════════════════════

              Nicht nur die Anzahl, sondern die Liste – Kind, Umfang,
              Eingewöhnung, Rabatt, Betrag. Eine Rechnung ist eine Forderung
              an eine Familie; wer sie auslöst, soll vorher an welche und
              über wie viel gesehen haben.

              Die Zeilen kommen aus derselben Vorschau, die der Server
              gerechnet hat – nicht aus einer zweiten Zusammenstellung, die
              danebenliegen könnte.
            */}
            {erstellenFrage && (
              <div className="o-dialog-grund" id="betreuung-erstellenfrage">
                <div className="o-dialog">
                  <h3 id="betreuung-erstellenfrage-titel">
                    {neuGewaehlt.length === 1
                      ? '1 Rechnungsentwurf erstellen?'
                      : `${neuGewaehlt.length} Rechnungsentwürfe erstellen?`}
                  </h3>
                  <p className="o-soft small">
                    {vorschau.bezeichnung} · Es entstehen Entwürfe. Keine Nummer, kein PDF,
                    kein Versand – das ist der Schritt danach.
                  </p>
                  <div className="o-tabelle">
                    <table className="o-data" id="betreuung-erstellenfrage-liste">
                      <thead>
                        <tr>
                          <th>Kind</th>
                          <th>Umfang</th>
                          <th className="o-num">Betrag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {neuGewaehlt.map((v) => (
                          <tr key={v.verhaeltnisId}>
                            <td>
                              {v.kindName}
                              <span className="o-soft small"> · {v.kundenname}</span>
                            </td>
                            <td className="o-soft small">
                              {v.positionen.map((p) => p.bezeichnung).join(' · ') || '–'}
                            </td>
                            <td className="o-num o-mono">{formatChf(v.nettoRp)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2}>Summe</th>
                          <th className="o-num o-mono">{formatChf(summe)}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="office-actions">
                    <button
                      type="button"
                      className="o-btn o-btn-primary"
                      id="betreuung-erstellen-los"
                      disabled={irgendwas}
                      onClick={() => void erstellen()}
                    >
                      Erstellen
                    </button>
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id="betreuung-erstellen-abbruch"
                      onClick={() => setErstellenFrage(false)}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/*
              ══════════════════════════════════════════════════════════════
              Vor dem Ersetzen: den Beleg nennen, der aufgehoben wird
              ══════════════════════════════════════════════════════════════

              Diese Frage ist einzeln und nicht gesammelt. Ein Storno ist
              keine Routine; er betrifft genau eine Rechnung, und die steht
              hier mit Nummer und Betrag.
            */}
            {korrektur && korrektur.vorhanden && (
              <div className="o-dialog-grund" id="betreuung-korrekturfrage">
                <div className="o-dialog">
                  <h3 id="betreuung-korrekturfrage-titel">
                    {korrektur.vorhanden.nummer
                      ? `Rechnung ${korrektur.vorhanden.nummer} ersetzen?`
                      : `Entwurf für ${korrektur.kindName} ersetzen?`}
                  </h3>
                  <p>
                    {korrektur.kindName} · {vorschau.bezeichnung} ·{' '}
                    <span className="o-mono">{formatChf(korrektur.vorhanden.totalRp)}</span>
                  </p>
                  <p>
                    {korrektur.vorhanden.nummer ? (
                      <>
                        Die bestehende Rechnung wird <strong>storniert</strong> – mit Grund und
                        Zeitpunkt in der Akte – und ein neuer Entwurf tritt an ihre Stelle.
                        Nummer, Zeilen und Protokoll der stornierten Rechnung bleiben
                        vollständig erhalten.
                      </>
                    ) : (
                      <>
                        Der bestehende Entwurf wird <strong>verworfen</strong> und neu
                        gerechnet. Ein Entwurf ist noch kein Beleg – er hat keine Nummer und
                        war nie bei den Eltern.
                      </>
                    )}
                  </p>
                  <p className="o-soft small">
                    Beides geschieht in einem Vorgang: Scheitert der neue Entwurf, bleibt kein
                    Storno zurück.
                  </p>
                  <div className="office-actions">
                    <button
                      type="button"
                      className="o-btn o-btn-primary"
                      id="betreuung-korrektur-los"
                      disabled={irgendwas}
                      onClick={() => void ersetzen(korrektur)}
                    >
                      {korrektur.vorhanden.nummer
                        ? 'Stornieren und neu erstellen'
                        : 'Verwerfen und neu erstellen'}
                    </button>
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id="betreuung-korrektur-abbruch"
                      onClick={() => setKorrektur(null)}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/*
              Die Freigabe fragt einmal – mit Anzahl und Folgen.

              Dieselbe Form wie bei den Sammelaktionen an den Belegen
              (`o-dialog-grund` > `o-dialog`), damit eine Bestätigung im Haus
              überall gleich aussieht. Was hier NICHT steht: eine Zeile je
              Kind. Die Rückmeldung danach ist einzeln, die Frage davor ist
              eine.
            */}
            {freigabeFrage && (
              <div className="o-dialog-grund" id="betreuung-freigabefrage">
                <div className="o-dialog">
                  <h3 id="betreuung-freigabefrage-titel">
                    {entwuerfe.length === 1
                      ? '1 Rechnung freigeben?'
                      : `${entwuerfe.length} Rechnungen freigeben?`}
                  </h3>
                  <p>
                    Jede bekommt eine Rechnungsnummer und ein PDF. Danach lässt sie sich nicht
                    mehr ändern, sondern nur noch stornieren. Versendet wird nichts – das ist der
                    Schritt danach.
                  </p>
                  <div className="office-actions">
                    <button
                      type="button"
                      className="o-btn o-btn-primary"
                      id="betreuung-freigabe-los"
                      disabled={irgendwas}
                      onClick={() => void sammelAbschluss()}
                    >
                      Freigeben
                    </button>
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id="betreuung-freigabe-abbruch"
                      onClick={() => setFreigabeFrage(false)}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="o-summe-zeile">
              <span className="o-soft">
                {gewaehlt.size} von {vorschau.vorschlaege.length} ausgewählt
              </span>
              <strong className="o-mono">{formatChf(summe)}</strong>
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="betreuung-erstellen"
                /*
                  Gezählt wird, was dieser Knopf WIRKLICH anfasst.

                  `gewaehlt.size` stand hier – und das sind seit der
                  Sammelfreigabe auch die Kinder, deren Entwurf schon liegt.
                  Der Knopf sagte «17 Rechnungsentwürfe erstellen» und legte
                  sechzehn an. Eine Zahl auf einem Knopf ist eine Zusage.
                */
                disabled={irgendwas || neuGewaehlt.length === 0}
                onClick={() => setErstellenFrage(true)}
              >
                {wartet('erstellen')
                  ? fortschritt
                    ? `Wird erstellt … ${fortschritt.fertig} von ${fortschritt.gesamt}`
                    : 'Wird erstellt …'
                  : neuGewaehlt.length === 1
                    ? '1 Rechnungsentwurf erstellen'
                    : `${neuGewaehlt.length} Rechnungsentwürfe erstellen`}
              </button>
              {/*
                Die Sammelfreigabe steht neben dem Vorbereiten, nicht in einem
                eigenen Bereich: Es ist derselbe Monat und derselbe Arbeitsgang,
                nur der Schritt danach. Sie erscheint erst, wenn es etwas
                freizugeben gibt – ein Knopf, der immer da ist und meistens
                nichts tut, erzieht zum Wegsehen.
              */}
              {entwuerfeGesamt > 0 && (
                <button
                  type="button"
                  className="o-btn o-btn-primary"
                  id="betreuung-abschluss"
                  disabled={irgendwas || entwuerfe.length === 0}
                  title={
                    entwuerfe.length === 0
                      ? `${entwuerfeGesamt} ${entwuerfeGesamt === 1 ? 'Entwurf wartet' : 'Entwürfe warten'} – zuerst die Kinder anhaken`
                      : undefined
                  }
                  onClick={() => setFreigabeFrage(true)}
                >
                  {wartet('abschluss')
                    ? 'Wird freigegeben …'
                    : entwuerfe.length === 0
                      ? entwuerfeGesamt === 1
                        ? '1 Entwurf wartet'
                        : `${entwuerfeGesamt} Entwürfe warten`
                      : entwuerfe.length === 1
                        ? '1 ausgewählte freigeben'
                        : `${entwuerfe.length} ausgewählte freigeben`}
                </button>
              )}
              {/*
                Herunterladen und Versenden erscheinen erst, wenn es
                abgeschlossene Rechnungen gibt – vorher wäre beides ein Knopf
                ohne Gegenstand.

                Das ZIP ist ein gewöhnlicher Verweis, wie der Jahresexport:
                Der Browser lädt, ohne dass die Anwendung eine Datei durch
                ihren eigenen Speicher schiebt.
              */}
              {abgeschlossene.length > 0 && (
                <>
                  {/*
                    Hier stand ein gewöhnlicher Verweis. Der lädt dorthin, wo
                    der Browser immer hinlädt – gemeldet wurde genau das: Der
                    Betrieb möchte beim Herunterladen selbst bestimmen, wohin.

                    `speichereDatei` öffnet deshalb den nativen «Speichern
                    unter …» Dialog, wo der Browser ihn hat, und nimmt sonst
                    den gewöhnlichen Weg. Ein Knopf statt eines Verweises,
                    weil ein Verweis nicht warten kann.

                    Geladen wird eine bestehende Datei. Es entsteht dabei
                    keine Rechnung und keine Forderung – der Weg ist ein GET.
                  */}
                  <button
                    type="button"
                    className="o-btn"
                    id="betreuung-zip"
                    disabled={irgendwas}
                    onClick={() => {
                      void (async () => {
                        setLaeuft('zip')
                        try {
                          const weg = await speichereDatei(
                            api.betreuungMonatPdfs(vorschau.monat.jahr, vorschau.monat.monat),
                            dateiname(['Betreuung', vorschau.bezeichnung], 'zip'),
                            () =>
                              api.betreuungMonatPdfsLaden(
                                vorschau.monat.jahr,
                                vorschau.monat.monat
                              )
                          )
                          if (weg === 'dialog') notify.ok('Gespeichert.')
                        } catch (e) {
                          notify.error(e instanceof Error ? e.message : 'Unbekannter Fehler')
                        } finally {
                          setLaeuft(null)
                        }
                      })()
                    }}
                  >
                    {wartet('zip')
                      ? 'Wird geladen …'
                      : abgeschlossene.length === 1
                        ? 'PDF herunterladen'
                        : `${abgeschlossene.length} PDFs herunterladen`}
                  </button>
                  <button
                    type="button"
                    className="o-btn"
                    id="betreuung-versand"
                    disabled={irgendwas}
                    onClick={() => void versandVorbereiten()}
                  >
                    {wartet('versand')
                      ? 'Wird vorbereitet …'
                      : abgeschlossene.length === 1
                        ? '1 Rechnung an die Eltern senden'
                        : `${abgeschlossene.length} Rechnungen an die Eltern senden`}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </section>

      {/* ── Betreuungsverhältnisse ─────────────────────────────────────── */}
      <section className="o-card" id="betreuung-stammdaten">
        <div className="o-card-head">
          <h2>Betreuungsverhältnisse</h2>
          <button
            type="button"
            className="o-btn"
            id="betreuung-neu"
            disabled={irgendwas}
            onClick={() => setFormular({ ...LEER })}
          >
            Kind erfassen
          </button>
        </div>

        {verhaeltnisse.length === 0 && (
          <p className="o-leer">
            Noch kein Kind erfasst. Ein Betreuungsverhältnis braucht Name, Geburtsdatum, den
            Rechnungsempfänger und die Betreuungstage pro Woche.
          </p>
        )}

        {verhaeltnisse.length > 0 && (
          <div className="o-tabelle">
          <table className="o-data">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Geburtsdatum</th>
                <th>Rechnungsempfänger</th>
                <th>Betreuung</th>
                <th>Rabatt</th>
                {/*
                  Die Aktionsspalte trägt eine Überschrift.

                  Eine leere Spaltenüberschrift über zwei Knöpfen liest sich
                  wie ein Darstellungsfehler, und auf einem schmalen Schirm
                  weiss niemand, dass dort noch etwas kommt.
                */}
                <th className="o-num">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {verhaeltnisse.map((v) => (
                <tr key={v.id}>
                  <td>{v.kindName}</td>
                  <td className="o-mono">{v.geburtsdatum}</td>
                  <td>{v.kundenname}</td>
                  <td>
                    {v.fixbetragRp !== null && v.fixbetragRp !== undefined
                      ? `Abweichend ${formatChf(v.fixbetragRp)}`
                      : betreuungstext(v.posten)}
                    {/*
                      Die Eingewöhnung steht in der Liste, weil sie den ersten
                      Monat bestimmt. Ohne diese Zeile sieht eine Rechnung über
                      500.00 statt der Pauschale wie ein Fehler aus, und
                      niemand findet den Grund, ohne das Kind zu öffnen.
                    */}
                    {(() => {
                      const f =
                        v.eingewoehnung === true
                          ? eingewoehnungszeitraum(v.eingewoehnungVon, v.eingewoehnungBis)
                          : null
                      return (
                        f && (
                          <div className="o-soft small">
                            Eingewöhnung {chTag(f.beginn)} bis {chTag(f.ende)}, regulär ab{' '}
                            {chTag(f.regulaerAb)}
                          </div>
                        )
                      )
                    })()}
                  </td>
                  <td>{v.geschwisterrabatt ? 'ja' : '–'}</td>
                  <td>
                    {/*
                      `office-actions` bricht die Knöpfe bei Platznot um,
                      statt die Tabelle über die Karte hinauszuschieben –
                      dieselbe Regel wie in jeder anderen Liste.

                      «Bearbeiten» ist hier die Hauptaktion und trägt deshalb
                      nicht die Geisterform: Ein Knopf, den man erst beim
                      Darüberfahren sieht, ist eine versteckte Aktion.
                    */}
                    <div className="office-actions">
                    <button
                      type="button"
                      className="o-btn"
                      id={`betreuung-bearbeiten-${v.id}`}
                      disabled={irgendwas}
                      onClick={() =>
                        setFormular({
                          id: v.id,
                          kindName: v.kindName,
                          geburtsdatum: v.geburtsdatum,
                          customerId: v.customerId,
                          beginn: v.beginn,
                          /*
                            Ein gespeichertes Enddatum stellt den Modus auf
                            «Endet am» und steht im Feld; ohne Enddatum
                            erscheint gar kein Feld.
                          */
                          endeModus: v.ende ? 'endet' : 'weiter',
                          ende: v.ende ?? '',
                          eingewoehnung: v.eingewoehnung ?? false,
                          eingewoehnungVon: v.eingewoehnungVon ?? '',
                          eingewoehnungBis: v.eingewoehnungBis ?? '',
                          geschwisterrabatt: v.geschwisterrabatt ?? false,
                          fixbetrag:
                            v.fixbetragRp === null || v.fixbetragRp === undefined
                              ? ''
                              : (v.fixbetragRp / 100).toFixed(2),
                          fixbetragText: v.fixbetragText ?? '',
                          hinweis: v.hinweis ?? '',
                          mengen: {
                            ganztag: teileVon(v.posten, 'ganztag'),
                            halbtag_ohne: teileVon(v.posten, 'halbtag_ohne'),
                            halbtag_mit: teileVon(v.posten, 'halbtag_mit')
                          }
                        })
                      }
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id={`betreuung-beenden-${v.id}`}
                      disabled={irgendwas}
                      onClick={() => void stilllegen(v)}
                    >
                      {wartet(`still-${v.id}`) ? 'Wird beendet …' : 'Beenden'}
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

      </section>

      {/*
        ── Erfassen und Bearbeiten ──────────────────────────────────────────

        Eine eigene Karte, nicht ein Anhang unter der Liste.

        Vorher stand das Formular im selben Abschnitt wie die Tabelle, und auf
        einem schmalen Schirm sah das aus wie eine zweite, zerquetschte Spalte
        derselben Liste. Eine Karte mit eigenem Titel sagt, was man gerade tut:
        «Kind erfassen» oder «Kind bearbeiten» – und beim Bearbeiten steht der
        Name des Kindes darüber, damit niemand das falsche ändert.
      */}
      {formular && (
        <section className="o-card" id="betreuung-formular-karte">
          <div className="o-card-head">
            <h2>{formular.id ? `${formular.kindName || 'Kind'} bearbeiten` : 'Kind erfassen'}</h2>
          </div>
          <div className="o-pad" id="betreuung-formular">
            {/*
              ══════════════════════════════════════════════════════════════
              Jedes Feld trägt sein Label über sich – und das ist eine
              Fehlerkorrektur, keine Geschmacksfrage
              ══════════════════════════════════════════════════════════════

              Hier stand `.o-werte`: ein Raster mit `150px minmax(0, 1fr)`,
              gefüllt mit abwechselnd Label und Feld. Das hält nur, solange
              die Reihenfolge stimmt UND die Breite reicht. Beides war nicht
              gegeben:

              Ein einziges Element, das kein Paar bildet – ein Hinweissatz,
              ein Mengentext –, verschiebt jedes Label-Feld-Paar danach um
              eine Zelle. Dann steht ein Wert neben der Beschriftung des
              NÄCHSTEN Feldes. Genau so entstand die Meldung «das Enddatum
              ist mit heute vorbelegt»: Vorbelegt ist «Betreuung ab» mit dem
              heutigen Tag, und das ist richtig – nur stand die Zahl neben
              der falschen Beschriftung.

              Und bei knapper Breite bricht eine Beschriftung wie «Betreuung
              bis (leer = läuft weiter)» in der 150-Pixel-Spalte über drei
              Zeilen, während das Feld oben bleibt. Dann rät man, was
              zusammengehört.

              `o-grid` + `o-field` ist das Muster, das jeder andere
              Bürobildschirm benutzt: Label, Feld und Hinweis liegen in EINEM
              Block, zusammen. Verrutschen kann da nichts, bei keiner Breite,
              und ein Hinweissatz mehr verschiebt nie wieder ein fremdes
              Feld.
            */}
            <div className="o-grid o-grid-2">
              <div className="o-field">
                <label htmlFor="betreuung-kind">Name des Kindes</label>
                <input
                  id="betreuung-kind"
                  value={formular.kindName}
                  onChange={(e) => setFormular({ ...formular, kindName: e.target.value })}
                  autoComplete="off"
                />
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-geburt">Geburtsdatum</label>
                <input
                  id="betreuung-geburt"
                  type="date"
                  value={formular.geburtsdatum}
                  onChange={(e) => setFormular({ ...formular, geburtsdatum: e.target.value })}
                  /*
                    Der Browser schlägt hier nichts vor: Ein Geburtsdatumsfeld,
                    das die zuletzt eingegebenen Werte anbietet, zeigt dem
                    nächsten Elternteil am Tresen die Daten des vorigen Kindes.
                  */
                  autoComplete="off"
                  lang="de-CH"
                />
                <Klartext wert={formular.geburtsdatum} feld="geburt" />
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-kunde">Rechnungsempfänger</label>
                <select
                  id="betreuung-kunde"
                  value={formular.customerId}
                  onChange={(e) => setFormular({ ...formular, customerId: e.target.value })}
                >
                  <option value="">– wählen –</option>
                  {kunden.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-beginn">Betreuung ab</label>
                <input
                  id="betreuung-beginn"
                  type="date"
                  value={formular.beginn}
                  onChange={(e) => setFormular({ ...formular, beginn: e.target.value })}
                  autoComplete="off"
                  lang="de-CH"
                />
                <Klartext wert={formular.beginn} feld="beginn" />
                <span className="o-soft small">Der Eintrittstag aus dem Vertrag.</span>
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-ende-modus">Ende der Betreuung</label>
                {/*
                  ════════════════════════════════════════════════════════════
                  Gefragt wird, nicht geraten
                  ════════════════════════════════════════════════════════════

                  Hier stand ein Datumsfeld, und «kein Enddatum» hiess: Feld
                  leer. Das ist derselbe Zustand wie «noch nichts
                  eingetragen», und ein `<input type="date">` zeigt ihn in
                  jedem Browser anders – in Safari stand zuletzt der heutige
                  Tag im Feld, obwohl im Formular nichts gesetzt war und der
                  Hinweis darunter «läuft weiter» sagte. Wer das sieht,
                  glaubt, die Betreuung ende heute. Für eine Kita ist das
                  kein Schönheitsfehler: Ein Kind fällt im Folgemonat aus der
                  Abrechnung.

                  Jede Lösung, die das leere Datumsfeld behält, verlässt sich
                  darauf, dass alle Browser es gleich anzeigen. Tun sie
                  nicht. Deshalb gibt es bei «Läuft weiter» jetzt überhaupt
                  kein Datumsfeld: kein Feld, keine Anzeige, kein
                  Browserverhalten, kein Missverständnis.
                */}
                <select
                  id="betreuung-ende-modus"
                  value={formular.endeModus}
                  onChange={(e) =>
                    setFormular({
                      ...formular,
                      endeModus: e.target.value === 'endet' ? 'endet' : 'weiter'
                    })
                  }
                >
                  <option value="weiter">Läuft weiter</option>
                  <option value="endet">Endet am</option>
                </select>
                {formular.endeModus === 'endet' && (
                  <input
                    id="betreuung-ende"
                    type="date"
                    value={formular.ende}
                    onChange={(e) => setFormular({ ...formular, ende: e.target.value })}
                    autoComplete="off"
                    lang="de-CH"
                  />
                )}
                {formular.endeModus === 'endet' && (
                  <Klartext wert={formular.ende} feld="ende" />
                )}
                <span className="o-soft small">
                  {formular.endeModus === 'weiter'
                    ? 'Kein Enddatum – die Betreuung läuft weiter, bis sie beendet wird.'
                    : formular.ende === ''
                      ? 'Bitte den letzten Betreuungstag wählen.'
                      : 'Der letzte Betreuungstag.'}
                </span>
              </div>

            </div>

            {/*
              ════════════════════════════════════════════════════════════════
              Die Eingewöhnung – ein Kästchen und zwei Kalender
              ════════════════════════════════════════════════════════════════

              Angekreuzt wie der Geschwisterrabatt, gleich darunter, in
              derselben Form (`cp-ankreuz`). Dahinter zwei Datumsfelder, die
              der Betrieb frei wählt.

              Hier stand eine Auswahlliste und KEIN Datumsfeld: Die
              Eingewöhnung begann zwangsweise am Eintrittstag und dauerte
              immer vierzehn Tage. Das deckt den häufigsten Fall und keinen
              anderen – in der Praxis kommt ein Kind zur Eingewöhnung auch
              vor dem Vertragsbeginn, und manches braucht zehn Tage statt
              vierzehn. Mit einem gerechneten Datum liess sich das nicht
              erfassen, und das Büro hätte es von Hand auf die Rechnung
              schreiben müssen – genau das, was die Automatik abschaffen
              sollte.

              Gerechnet und nicht eingegeben bleibt nur, was sich wirklich
              ableiten lässt: der erste reguläre Betreuungstag (der Tag nach
              dem Ende) und die Dauer. Dafür steht dieselbe Funktion, mit der
              der Server abrechnet – `eingewoehnungszeitraum` im gemeinsamen
              Paket. Was auf dem Schirm steht, ist damit das, was auf die
              Rechnung kommt.
            */}
            <label className="cp-ankreuz" htmlFor="betreuung-eingewoehnung">
              <input
                type="checkbox"
                id="betreuung-eingewoehnung"
                autoComplete="off"
                checked={formular.eingewoehnung}
                onChange={(e) => {
                  const an = e.target.checked
                  /*
                    Beim Ankreuzen das Enddatum vorschlagen, wenn ein Beginn
                    dasteht und noch kein Ende: vierzehn Tage, der häufigste
                    Fall. Ein Vorschlag und keine Vorschrift – das Feld
                    bleibt bearbeitbar, und ein bereits eingetragenes Ende
                    wird nie überschrieben.
                  */
                  const von = formular.eingewoehnungVon || formular.beginn
                  setFormular({
                    ...formular,
                    eingewoehnung: an,
                    eingewoehnungVon: an ? von : formular.eingewoehnungVon,
                    eingewoehnungBis:
                      an && formular.eingewoehnungBis === '' && von !== ''
                        ? eingewoehnungsendeVorschlag(von)
                        : formular.eingewoehnungBis
                  })
                }}
              />
              <span>
                Eingewöhnungsphase – Pauschale {formatChf(EINGEWOEHNUNG_PAUSCHALE_RP)}, einmalig.
                In dieser Zeit wird keine reguläre Betreuung verrechnet.
              </span>
            </label>

            {formular.eingewoehnung && (
              <div className="o-grid o-grid-2" id="betreuung-eingewoehnung-zeitraum">
                <div className="o-field">
                  <label htmlFor="betreuung-eingewoehnung-von">Eingewöhnung ab</label>
                  <input
                    id="betreuung-eingewoehnung-von"
                    type="date"
                    value={formular.eingewoehnungVon}
                    onChange={(e) => {
                      /*
                        Ein neuer Beginn schlägt das Ende neu vor, solange
                        keines gesetzt ist. Steht schon eines da, bleibt es –
                        es ist eine Angabe des Betriebs und keine Ableitung.
                      */
                      const von = e.target.value
                      setFormular({
                        ...formular,
                        eingewoehnungVon: von,
                        eingewoehnungBis:
                          formular.eingewoehnungBis === '' && von !== ''
                            ? eingewoehnungsendeVorschlag(von)
                            : formular.eingewoehnungBis
                      })
                    }}
                    autoComplete="off"
                    lang="de-CH"
                  />
                  <Klartext wert={formular.eingewoehnungVon} feld="eingewoehnung-von" />
                  <span className="o-soft small">
                    Frei wählbar – auch vor dem Eintrittstag.
                  </span>
                </div>

                <div className="o-field">
                  <label htmlFor="betreuung-eingewoehnung-bis">Eingewöhnung bis</label>
                  <input
                    id="betreuung-eingewoehnung-bis"
                    type="date"
                    value={formular.eingewoehnungBis}
                    onChange={(e) =>
                      setFormular({ ...formular, eingewoehnungBis: e.target.value })
                    }
                    /*
                      ──────────────────────────────────────────────────────
                      Die Grenze steht im Kalender selbst
                      ──────────────────────────────────────────────────────

                      Die Pauschale gilt für höchstens vierzehn Kalendertage.
                      `min` und `max` sagen das dem Datumswähler: Vor dem
                      Beginn und nach dem vierzehnten Tag lässt er gar nicht
                      erst etwas auswählen – kein Fehler, den jemand erst nach
                      dem Speichern liest.

                      Das ersetzt die Prüfung am Server NICHT. `max` ist eine
                      Bequemlichkeit im Browser; die Regel steht in
                      `pruefeEingabe` (services/betreuung.ts), und sie gilt
                      auch für einen Aufruf, der dieses Formular nie gesehen
                      hat.
                    */
                    min={formular.eingewoehnungVon || undefined}
                    max={
                      formular.eingewoehnungVon
                        ? eingewoehnungsendeVorschlag(formular.eingewoehnungVon)
                        : undefined
                    }
                    autoComplete="off"
                    lang="de-CH"
                  />
                  <Klartext wert={formular.eingewoehnungBis} feld="eingewoehnung-bis" />
                  <span className="o-soft small">
                    Der letzte Tag, er zählt mit – höchstens {EINGEWOEHNUNG_TAGE} Tage ab
                    Beginn.
                  </span>
                </div>

              </div>
            )}

            {/*
              Der gerechnete Teil, in einem Satz: Dauer und erster regulärer
              Tag. Er steht UNTER dem Raster und nicht als dritte Zelle darin
              – eine Zelle über die volle Breite bräuchte eine eigene
              Rasterregel, und ein Satz unter zwei Feldern liest sich auf dem
              Telefon ohnehin besser als eine halbe Spalte.

              Und er sagt, was nicht geht: Mehr als vierzehn Tage lässt der
              Kalender nicht zu, und wer es trotzdem eintippt, liest hier den
              Grund, bevor er auf einen grauen Knopf drückt.
            */}
            {formular.eingewoehnung &&
              (() => {
                const f = eingewoehnungszeitraum(
                  formular.eingewoehnungVon || null,
                  formular.eingewoehnungBis || null
                )
                if (!f) {
                  return (
                    <p className="o-soft small" id="betreuung-eingewoehnung-daten">
                      {formular.eingewoehnungVon === '' || formular.eingewoehnungBis === ''
                        ? 'Bitte beide Tage der Eingewöhnung wählen.'
                        : 'Der letzte Tag der Eingewöhnung liegt vor ihrem ersten.'}
                    </p>
                  )
                }
                if (f.tage > EINGEWOEHNUNG_TAGE) {
                  return (
                    <p className="o-soft small" id="betreuung-eingewoehnung-daten">
                      {f.tage} Kalendertage – die Pauschale von{' '}
                      {formatChf(EINGEWOEHNUNG_PAUSCHALE_RP)} gilt für höchstens{' '}
                      {EINGEWOEHNUNG_TAGE} Tage, also spätestens bis zum{' '}
                      {chTag(eingewoehnungsendeVorschlag(formular.eingewoehnungVon))}. Kürzer
                      ist erlaubt, länger nicht.
                    </p>
                  )
                }
                return (
                  <p className="o-soft small" id="betreuung-eingewoehnung-daten">
                    {f.tage} Kalendertage ({chTag(f.beginn)} bis {chTag(f.ende)}) · reguläre
                    Betreuung ab {chTag(f.regulaerAb)}, der erste reguläre Monat anteilig ab
                    diesem Tag.
                    {f.tage < EINGEWOEHNUNG_TAGE && (
                      <>
                        {' '}
                        Kürzer als die üblichen {EINGEWOEHNUNG_TAGE} Tage – die Pauschale
                        bleibt {formatChf(EINGEWOEHNUNG_PAUSCHALE_RP)}, und der Zeitraum steht
                        auf der Rechnung.
                      </>
                    )}
                  </p>
                )
              })()}

            <p className="o-soft small">
              Betreuungstage pro Woche, in Viertelschritten. Der Preis je Tag und Monat kommt
              aus dem Leistungskatalog; ob Baby- oder Kleinkindtarif gilt, entscheidet das
              Geburtsdatum. Ist eine Menge aus mehreren Teilen abgemacht – etwa ¾ und ½ –,
              lassen sich die Teile einzeln eintragen: Verrechnet wird ihre Summe in einer
              Position, und auf der Rechnung stehen die Teile daneben.
            </p>
            <div className="o-grid o-grid-3">
              {ARTEN.map((a) => {
                const teile = formular.mengen[a.art]
                const summe = summeTeile(teile)
                const aufteilung = aufteilungstext(
                  teile.map(alsMenge).filter((z): z is number => z !== null)
                )
                const setze = (naechste: string[]): void =>
                  setFormular({
                    ...formular,
                    mengen: {
                      ...formular.mengen,
                      [a.art]: naechste.length === 0 ? [''] : naechste
                    }
                  })
                return (
                  <div key={a.art} className="o-field">
                    <label htmlFor={`betreuung-menge-${a.art}`}>{a.label}</label>
                    {teile.map((wert, i) => {
                      const zahl = alsMenge(wert)
                      /*
                        Eine Menge, die nicht auf dem Viertel liegt, bleibt
                        wählbar.

                        Sie kann aus einer früheren Vereinbarung stammen. Sie
                        stillschweigend auf das nächste Viertel zu ziehen würde
                        den Preis ändern, und zwar beim Speichern eines
                        Formulars, das jemand nur aufgeschlagen hatte.
                      */
                      const fremd =
                        zahl !== null && !STUFEN.some((x) => Math.abs(x - zahl) < 1e-9)
                      return (
                        <div className="o-feld-mit-aktion" key={`${a.art}-${i}`}>
                          <select
                            id={
                              i === 0
                                ? `betreuung-menge-${a.art}`
                                : `betreuung-menge-${a.art}-${i + 1}`
                            }
                            value={wert === '' ? '0' : wert}
                            onChange={(ev) =>
                              setze(
                                teile.map((alt, j) =>
                                  j === i ? (ev.target.value === '0' ? '' : ev.target.value) : alt
                                )
                              )
                            }
                          >
                            {fremd && zahl !== null && (
                              <option value={String(zahl)}>{alsBruch(zahl)} (vereinbart)</option>
                            )}
                            {STUFEN.map((x) => (
                              <option key={x} value={String(x)}>
                                {x === 0 ? '–' : alsBruch(x)}
                              </option>
                            ))}
                          </select>
                          {i > 0 && (
                            <button
                              type="button"
                              className="o-btn o-btn-ghost"
                              id={`betreuung-teil-weg-${a.art}-${i + 1}`}
                              disabled={irgendwas}
                              onClick={() => setze(teile.filter((_, j) => j !== i))}
                            >
                              Teil entfernen
                            </button>
                          )}
                        </div>
                      )
                    })}
                    {/*
                      Der Knopf erscheint erst, wenn der erste Teil gewählt
                      ist: Ein zweites leeres Feld über einem leeren ersten
                      hilft niemandem.
                    */}
                    {summe !== null && teile.length < TEILE_MAX && (
                      <button
                        type="button"
                        className="o-btn o-btn-ghost o-feld-aktion"
                        id={`betreuung-teil-dazu-${a.art}`}
                        disabled={irgendwas}
                        onClick={() => setze([...teile, ''])}
                      >
                        Teilmenge hinzufügen
                      </button>
                    )}
                    {summe !== null && (
                      <span className="o-soft small" data-mengentext={a.art}>
                        {mengentext(a.art, summe)}
                        {aufteilung === '' ? '' : ` · vereinbart als ${aufteilung}`}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>

            <label className="cp-ankreuz" htmlFor="betreuung-rabatt">
              <input
                type="checkbox"
                id="betreuung-rabatt"
                autoComplete="off"
                checked={formular.geschwisterrabatt}
                onChange={(e) =>
                  setFormular({ ...formular, geschwisterrabatt: e.target.checked })
                }
              />
              <span>
                Geschwisterrabatt gewähren. Der Satz je Betreuungstag steht im
                Leistungskatalog.
              </span>
            </label>

            {/*
              Die Ausnahme, als Ausnahme gesetzt.

              Fachlich ersetzt ein Betrag in diesem Feld die Tarifrechnung
              vollständig – Betreuungstage, Alterstarif und Geschwisterrabatt
              werden dann nicht mehr angewendet (`kitaPositionen` in
              services/kita.ts). Deshalb steht der Block unter einer eigenen
              Überschrift und nicht zwischen den Stammangaben: Was den
              Normalfall aushebelt, soll man nicht versehentlich ausfüllen.
            */}
            <h3>Ausnahme vom Tarif</h3>
            <div className="o-grid o-grid-2">
              <div className="o-field">
                <label htmlFor="betreuung-fix">Abweichender Monatsbetrag in CHF</label>
                <input
                  id="betreuung-fix"
                  inputMode="decimal"
                  placeholder="leer = nach Tarif"
                  value={formular.fixbetrag}
                  onChange={(e) => setFormular({ ...formular, fixbetrag: e.target.value })}
                  autoComplete="off"
                />
                <span className="o-soft small" id="betreuung-fix-hinweis">
                  Leer lassen ist der Normalfall: Dann rechnen Tarif,
                  Betreuungstage, Altersklasse und Geschwisterrabatt. Ein Betrag
                  hier ersetzt diese Berechnung vollständig und wird nicht
                  rabattiert.
                </span>
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-fixtext">Text dafür auf der Rechnung</label>
                <input
                  id="betreuung-fixtext"
                  value={formular.fixbetragText}
                  onChange={(e) => setFormular({ ...formular, fixbetragText: e.target.value })}
                  placeholder="z. B. Betreuung nach Absprache"
                  autoComplete="off"
                />
              </div>

              <div className="o-field">
                <label htmlFor="betreuung-hinweis">Hinweis zur Vereinbarung</label>
                <input
                  id="betreuung-hinweis"
                  value={formular.hinweis}
                  onChange={(e) => setFormular({ ...formular, hinweis: e.target.value })}
                  placeholder="z. B. Kündigungsfrist drei Monate auf Monatsende"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="o-dialog-fuss">
              <button
                type="button"
                className="o-btn o-btn-ghost"
                onClick={() => setFormular(null)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="betreuung-speichern"
                /*
                  Die Pflichtfelder stehen hier, damit das Formular sie selbst
                  nennt. Ohne `beginn` wies der Server mit einer Meldung über
                  ein Datumsformat ab – richtig, aber unverständlich für
                  jemanden, der das Feld einfach übersehen hat.
                */
                disabled={
                  irgendwas ||
                  formular.kindName.trim() === '' ||
                  formular.geburtsdatum === '' ||
                  formular.customerId === '' ||
                  formular.beginn === '' ||
                  /*
                    «Endet am» ohne Tag ist keine Angabe, sondern eine halbe.
                    Sie stillschweigend als «läuft weiter» zu speichern wäre
                    das Gegenteil dessen, was jemand gerade gewählt hat.
                  */
                  (formular.endeModus === 'endet' && formular.ende === '') ||
                  /*
                    Dieselbe Regel für die Eingewöhnung: Ein gesetztes
                    Häkchen ohne Zeitraum ist eine halbe Angabe. Der Server
                    weist sie mit einem verständlichen Satz ab – aber ihn
                    dafür erst anzurufen, nachdem jemand alles andere
                    ausgefüllt hat, ist der schlechtere Weg. Das Formular
                    nennt seine Pflichtfelder selbst.
                  */
                  (formular.eingewoehnung &&
                    (formular.eingewoehnungVon === '' ||
                      formular.eingewoehnungBis === '' ||
                      /*
                        Und ein Zeitraum, den der Server ohnehin abweist:
                        verdreht oder länger als vierzehn Tage. Ihn hier
                        schon grau zu machen spart den Weg über eine
                        Fehlermeldung – die Regel selbst steht trotzdem am
                        Server.
                      */
                      (eingewoehnungszeitraum(
                        formular.eingewoehnungVon,
                        formular.eingewoehnungBis
                      )?.tage ?? 0) < 1 ||
                      (eingewoehnungszeitraum(
                        formular.eingewoehnungVon,
                        formular.eingewoehnungBis
                      )?.tage ?? 0) > EINGEWOEHNUNG_TAGE))
                }
                onClick={() => void speichern()}
              >
                {wartet('speichern') ? 'Wird gespeichert …' : 'Speichern'}
              </button>
            </div>
          </div>
        </section>
      )}

      <p className="o-soft small">
        Zusatzbetreuung – ein einzelner Tag, ein Mittagessen – wird nach Tarifblatt am
        Betreuungstag bar gegen Quittung bezahlt. Soll sie trotzdem auf die Monatsrechnung, wird
        sie dort als Position erfasst; dieser Bildschirm nimmt sie nicht automatisch dazu, damit
        bar Bezahltes nicht zweimal verrechnet wird.
      </p>
      <p className="o-soft small">Angemeldet als {session.email}.</p>

      {/*
        Der gemeinsame Editor – die Zwischenstufe, die hier gefehlt hat.

        Er bekommt die Vorlage MIT Platzhaltern, nicht die gefüllte Fassung:
        Es gibt nicht eine Rechnungsnummer, sondern eine je Familie. Gefüllt
        wird beim Versand, je Beleg, auf dem Server.
      */}
      {serienmail && (
        <SerienMailEditor
          titel="Monatsrechnungen an die Eltern senden"
          empfaenger={serienempfaenger}
          firmenname={firma}
          vorlage={VORLAGE_RECHNUNG}
          aufSenden={sammelVersand}
          aufAbbrechen={() => setSerienmail(false)}
        />
      )}
    </div>
  )
}
